import React, { useEffect, useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import { CSSTransition } from "react-transition-group";
import { useNavigate } from "react-router";
import { SCREEN_PATHS } from "../../../constants";
import { ScreenContentWrapper } from "../../shared/ScreenContentWrapper";

import clipboardImg1x from "../../assets/ani-clipboard-1x.png";
import clipboardImg15x from "../../assets/ani-clipboard-1.5x.png";
import clipboardImg2x from "../../assets/ani-clipboard-2x.png";
import clipboardImg3x from "../../assets/ani-clipboard-3x.png";
import courtImg1x from "../../assets/ani-court-1x.png";
import courtImg15x from "../../assets/ani-court-1.5x.png";
import courtImg2x from "../../assets/ani-court-2x.png";
import courtImg3x from "../../assets/ani-court-3x.png";
import scaleImg1x from "../../assets/ani-scale-1x.png";
import scaleImg15x from "../../assets/ani-scale-1.5x.png";
import scaleImg2x from "../../assets/ani-scale-2x.png";
import scaleImg3x from "../../assets/ani-scale-3x.png";
import folder1x from "../../assets/ani-folder-1x.png";
import folder15x from "../../assets/ani-folder-1.5x.png";
import folder2x from "../../assets/ani-folder-2x.png";
import folder3x from "../../assets/ani-folder-3x.png";
import checkBall1x from "../../assets/ani-checkball-1x.png";
import checkBall15x from "../../assets/ani-checkball-1.5x.png";
import checkBall2x from "../../assets/ani-checkball-2x.png";
import checkBall3x from "../../assets/ani-checkball-3x.png";

import "./animations.css";
import { PreProdOnlyWrapper } from "../../shared/PreProdOnlyWrapper";
import { useProbateContext } from "../../../ProbateContextWrapper";
import { useSearchParams } from "react-router-dom";

const bulletItemStyle = {
  display: "flex",
  alignItems: "center",
  width: {
    xs: "90%",
    sm: "80%",
    md: "60%",
  },
  mx: "auto",
  my: 2,
  textAlign: "left",
  "& img": {
    display: "inline-block",
    marginRight: 1,
    verticalAlign: "middle",
  },
} as const;

const animatedElement = {
  position: "absolute",
  textAlign: "center",
  mx: "auto",
  left: 0,
  right: 0,
} as const;

const animatedIcon = {
  height: "120px",
};

interface IProps {
  finalPath?: string
}

// Animation use react-transition-group library
//  https://reactcommunity.org/react-transition-group/css-transition
//  https://www.npmjs.com/package/react-transition-group
export const AssembleScreen = (props?:IProps): JSX.Element => {
  const navigate = useNavigate();
  // const { clearFieldsAnswered } = useProbateContext()
  const [searchParams] = useSearchParams();

  // animation trackers
  const [scene1Icon, setScene1Icon] = useState(false);
  const [scene1Title, setScene1Title] = useState(false);
  const [scene2Icon, setScene2Icon] = useState(false);
  const [scene2Title, setScene2Title] = useState(false);
  const [scene3Icon, setScene3Icon] = useState(false);
  const [scene3Title, setScene3Title] = useState(false);
  const [scene3Bullet1, setScene3Bullet1] = useState(false);
  const [scene3Bullet2, setScene3Bullet2] = useState(false);
  const [scene3Bullet3, setScene3Bullet3] = useState(false);
  const [scene4Icon, setScene4Icon] = useState(false);
  const [scene4Title, setScene4Title] = useState(false);

  const handleComplete = async (): Promise<boolean> => {
    let path = props?.finalPath || SCREEN_PATHS.probateConfirm
    if(searchParams) {
      path += "?" + searchParams.toString()
    }
    console.log("nav to " + path)
    navigate(path);
    return true;
  };

  useEffect(() => {
    setTimeout(() => {
      // clearFieldsAnswered?.();
      // start the animation sequence
      setScene1Icon(true);
    }, 500);
  }, []);

  // use 0.5 of screens larger than 1000, 0.8 for screens smaller than that
  const stageHeight =
    window.innerHeight -
    window.innerHeight * (window.innerHeight > 1000 ? 0.5 : 0.2);

  return (
    <ScreenContentWrapper pageSuffix="Assemble">
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            position: "relative",
            height: stageHeight,
            width: "100%",
            textAlign: "center",
          }}
        >
          {/* clipboard icon */}
          <CSSTransition
            in={scene1Icon}
            timeout={1000}
            classNames="icon"
            unmountOnExit
            onEntered={() => {
              // slide in the title
              setScene1Title(true);
            }}
          >
            <Box sx={{ ...animatedElement, ...animatedIcon }}>
              <img
                src={clipboardImg1x}
                srcSet={`${clipboardImg15x} 1.5x, ${clipboardImg2x} 2x, ${clipboardImg3x} 3x`}
                alt="clipboard"
              />
            </Box>
          </CSSTransition>
          {/* clipboard icon title */}
          <CSSTransition
            in={scene1Title}
            timeout={500}
            classNames="title"
            unmountOnExit
            onEntered={() => {
              setTimeout(() => {
                // slide both out icon and title
                setScene1Icon(false);
                setScene1Title(false);
              }, 3000);
            }}
            onExited={() => {
              // slide in the next icon
              setScene2Icon(true);
            }}
          >
            <Box sx={animatedElement}>
              <Typography variant="h3">Reviewing your answers...</Typography>
            </Box>
          </CSSTransition>
          {/* ----------- next group of elements -----------  */}
          {/* court icon */}
          <CSSTransition
            in={scene2Icon}
            timeout={1000}
            classNames="icon"
            unmountOnExit
            onEntered={() => {
              // slide in the title
              setScene2Title(true);
            }}
          >
            <Box sx={{ ...animatedElement, ...animatedIcon }}>
              <img
                src={courtImg1x}
                srcSet={`${courtImg15x} 1.5x, ${courtImg2x} 2x, ${courtImg3x} 3x`}
                alt="court"
              />
            </Box>
          </CSSTransition>
          {/* court icon title */}
          <CSSTransition
            in={scene2Title}
            timeout={500}
            classNames="title"
            unmountOnExit
            onEntered={() => {
              setTimeout(() => {
                // slide both out icon and title
                setScene2Icon(false);
                setScene2Title(false);
              }, 3000);
            }}
            onExited={() => {
              // slide in the next icon
              setScene3Icon(true);
            }}
          >
            <Box sx={animatedElement}>
              <Typography variant="h3">
                Talking to your probate court...
              </Typography>
            </Box>
          </CSSTransition>
          {/* ----------- next group of elements -----------  */}
          {/* scale icon */}
          <CSSTransition
            in={scene3Icon}
            timeout={1000}
            classNames="icon"
            unmountOnExit
            onEntered={() => {
              // slide in the title
              setScene3Title(true);
            }}
          >
            <Box sx={{ ...animatedElement, ...animatedIcon }}>
              <img
                src={scaleImg1x}
                srcSet={`${scaleImg15x} 1.5x, ${scaleImg2x} 2x, ${scaleImg3x} 3x`}
                alt="scale"
                style={{
                  display: "inline-block",
                  marginTop: "30px",
                }}
              />
            </Box>
          </CSSTransition>
          {/* clipboard icon title */}
          <CSSTransition
            in={scene3Title}
            timeout={500}
            classNames="title"
            unmountOnExit
            onEntered={() => {
              setTimeout(() => {
                // fade in first bullet
                setScene3Bullet1(true);
              }, 500);
            }}
            onExited={() => {
              setScene4Icon(true);
            }}
          >
            <Box sx={animatedElement}>
              <Typography
                variant="h3"
                sx={{
                  mb: 3,
                }}
              >
                Determining the type of probate required...
              </Typography>
              <CSSTransition
                in={scene3Bullet1}
                timeout={500}
                classNames="bullet"
                unmountOnExit
                onEntered={() => {
                  setTimeout(() => {
                    // fade in the next bullet
                    setScene3Bullet2(true);
                  }, 500);
                }}
              >
                <Box sx={bulletItemStyle}>
                  <img
                    src={checkBall1x}
                    srcSet={`${checkBall15x} 1.5x, ${checkBall2x} 2x, ${checkBall3x} 3x`}
                    alt="check"
                  />
                  <Typography variant="body1">
                    Reviewing Your State Probate statutes
                  </Typography>
                </Box>
              </CSSTransition>
              <CSSTransition
                in={scene3Bullet2}
                timeout={500}
                classNames="bullet"
                unmountOnExit
                onEntered={() => {
                  setTimeout(() => {
                    // fade in the next bullet
                    setScene3Bullet3(true);
                  }, 500);
                }}
              >
                <Box sx={bulletItemStyle}>
                  <img
                    src={checkBall1x}
                    srcSet={`${checkBall15x} 1.5x, ${checkBall2x} 2x, ${checkBall3x} 3x`}
                    alt="check"
                  />
                  <Typography variant="body1">
                    Reviewing your qualifications
                  </Typography>
                </Box>
              </CSSTransition>
              <CSSTransition
                in={scene3Bullet3}
                timeout={500}
                classNames="bullet"
                unmountOnExit
                onEntered={() => {
                  setTimeout(() => {
                    // slide both out icon and title
                    //  & fade bullets
                    setScene3Icon(false);
                    setScene3Title(false);
                    setScene3Bullet1(false);
                    setScene3Bullet2(false);
                    setScene3Bullet3(false);
                  }, 3000);
                }}
                onExited={() => {
                  // slide in the next icon
                  setScene4Icon(true);
                }}
              >
                <Box sx={bulletItemStyle}>
                  <img
                    src={checkBall1x}
                    srcSet={`${checkBall15x} 1.5x, ${checkBall2x} 2x, ${checkBall3x} 3x`}
                    alt="check"
                  />
                  <Typography variant="body1">
                    Verifying the types and values of the estate’s assets
                  </Typography>
                </Box>
              </CSSTransition>
            </Box>
          </CSSTransition>
          {/* ----------- next group of elements -----------  */}
          {/* folder icon */}
          <CSSTransition
            in={scene4Icon}
            timeout={1000}
            classNames="icon"
            unmountOnExit
            onEntered={() => {
              // slide in the title
              setScene4Title(true);
            }}
          >
            <Box sx={{ ...animatedElement, ...animatedIcon }}>
              <img
                src={folder1x}
                srcSet={`${folder15x} 1.5x, ${folder2x} 2x, ${folder3x} 3x`}
                alt="clipboard"
              />
            </Box>
          </CSSTransition>
          {/* folder icon title */}
          <CSSTransition
            in={scene4Title}
            timeout={500}
            classNames="title"
            unmountOnExit
            onEntered={() => {
              setTimeout(() => {
                // slide both out icon and title
                setScene4Icon(false);
                setScene4Title(false);
              }, 4000);
            }}
            onExited={() => {
              // handleComplete(false, false); // swap for this one to make debugging easier
              handleComplete();
            }}
          >
            <Box sx={animatedElement}>
              <Typography variant="h3">
                Assembling your Probate packet...
              </Typography>
            </Box>
          </CSSTransition>
        </Box>
        <PreProdOnlyWrapper>
          <Button onClick={() => handleComplete()}>Skip</Button>
        </PreProdOnlyWrapper>
      </Box>
    </ScreenContentWrapper>
  );
};
